/**
 * RcpScaffoldPage — System · Retail Customer Portal (Phase 2).
 *
 * Read-only readiness / status panel for the future customer-facing portal
 * (RCP). Ported 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/system/RcpScaffoldPanel.tsx) — the
 * operational app surfaces no config/scaffold panels. Read-only, no writes.
 *
 * Surfaces the four pieces a future RCP build agent needs at a glance:
 *   1. The `rcp.enabled` master toggle + pcbportal tenant binding (read live
 *      from SystemSettings).
 *   2. The /v3/portal/* reserved route table with current return semantics.
 *   3. The customer-as-actor SQL shape verification table.
 *   4. The Phase-2 cutover checklist (incl. the AAD app-reg shape doc path).
 *
 * Per Bill's binding rule (project_rcp_only_scaffolding_allowed), this is the
 * ONE acceptable scaffolded surface in v3.0 — every other staff-facing screen
 * is fully functional.
 *
 * Read: client.retail.config.systemSettings()
 *       → GET /v3/config/system-settings (Anonymous; ambient bearer)
 *
 * Wire note (bug fixed in port): the GET returns an ARRAY of SystemSetting
 * rows ({ settingKey, settingValue, ... }), filtered to non-sensitive/active
 * — NOT a flat key/value dict. The original ROP-teams panel read
 * `data['rcp.enabled']` against the array, so its toggle + tenant binding
 * ALWAYS rendered the disabled/"—" defaults regardless of DB state. This
 * port matches rows on `settingKey`. Seed: 16-rcp-scaffolding-seeds.sql.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';

// ── Live status (read from SystemSettings rcp.* rows) ──────────────────────

interface RcpStatus {
  enabled: boolean;
  tenantId: string | null;
  tenantDomain: string | null;
  routePrefix: string | null;
}

// The /v3/config/system-settings GET returns this row shape (non-sensitive,
// active rows only). Mirrors dbo.SystemSettings; we only read a few fields.
interface SystemSettingRow {
  settingKey: string;
  settingValue: string | null;
}

function asBool(raw: string | null): boolean {
  if (raw === null) return false;
  const v = raw.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

// ── Static reference tables (verbatim from the ROP-teams scaffold) ─────────

interface PortalRouteRow {
  method: 'GET' | 'POST';
  path: string;
  purpose: string;
  current: 'phase-2-pending';
}

interface SqlShapeRow {
  briefName: string;
  actualColumn: string;
  status: 'OK' | 'N/A' | 'EXTEND';
  note: string;
}

const PORTAL_ROUTES: ReadonlyArray<PortalRouteRow> = [
  {
    method: 'GET',
    path: '/v3/portal/health',
    purpose: 'Portal liveness check',
    current: 'phase-2-pending',
  },
  {
    method: 'POST',
    path: '/v3/portal/start-guest-apply',
    purpose: 'Customer kicks off a self-apply',
    current: 'phase-2-pending',
  },
  {
    method: 'GET',
    path: '/v3/portal/account-summary',
    purpose: 'Customer reads their cart / accounts',
    current: 'phase-2-pending',
  },
  {
    method: 'POST',
    path: '/v3/portal/secure-doc-request',
    purpose: 'Customer uploads / requests a document',
    current: 'phase-2-pending',
  },
  {
    method: 'POST',
    path: '/v3/portal/beneficiary-update',
    purpose: 'Customer self-service beneficiary edit',
    current: 'phase-2-pending',
  },
];

const SQL_SHAPE: ReadonlyArray<SqlShapeRow> = [
  {
    briefName: 'Cart.OriginRole',
    actualColumn: 'dbo.Carts.Origin (nvarchar(32), free-text)',
    status: 'OK',
    note: "Free-text already accepts 'customer-portal'. No CHECK extension needed.",
  },
  {
    briefName: 'SessionEvent.actorType',
    actualColumn: 'dbo.SessionEvents.Actor (nvarchar(256), free-text)',
    status: 'OK',
    note: "Free-text already accepts 'customer:<aadObjectId>'. No CHECK extension needed.",
  },
  {
    briefName: 'Document.captureSource',
    actualColumn: '(no Document table — SharePoint ao_Documents library)',
    status: 'N/A',
    note: 'Documents stored in SharePoint; folder convention /customer-uploads/<cartId>/...',
  },
  {
    briefName: 'NotificationTemplate.recipientType',
    actualColumn: 'NotificationTemplates (CategoryCode + ChannelCode route)',
    status: 'N/A',
    note: "No recipientType column — category like 'customer-*' covers routing today.",
  },
];

const AAD_SHAPE_DOC = 'docs/port-design/rcp-aad-app-shape.md';

// ── Columns ────────────────────────────────────────────────────────────────

const ROUTE_COLUMNS: ReadonlyArray<DataTableColumn<PortalRouteRow>> = [
  { header: 'Method', width: 80, cell: (r) => <code className="cell-mono">{r.method}</code> },
  { header: 'Path', width: 280, cell: (r) => <code className="cell-mono">{r.path}</code> },
  { header: 'Purpose', cell: (r) => <span className="cell-desc">{r.purpose}</span> },
  { header: 'Current behavior', width: 170, cell: (r) => <Pill tone="info">{r.current}</Pill> },
];

function sqlStatusTone(status: SqlShapeRow['status']): 'ok' | 'warn' | 'muted' {
  if (status === 'OK') return 'ok';
  if (status === 'EXTEND') return 'warn';
  return 'muted';
}

const SQL_COLUMNS: ReadonlyArray<DataTableColumn<SqlShapeRow>> = [
  { header: 'Brief calls for', width: 220, cell: (r) => <code className="cell-mono">{r.briefName}</code> },
  { header: 'Actual column', width: 300, cell: (r) => <code className="cell-mono">{r.actualColumn}</code> },
  { header: 'Status', width: 90, cell: (r) => <Pill tone={sqlStatusTone(r.status)}>{r.status}</Pill> },
  { header: 'Note', cell: (r) => <span className="cell-desc">{r.note}</span> },
];

// ── Page ─────────────────────────────────────────────────────────────────

export function RcpScaffoldPage(): JSX.Element {
  const client = useApiClient();
  const [status, setStatus] = useState<RcpStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setStatus(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.systemSettings();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const byKey = new Map<string, string | null>();
          for (const row of resp.data as unknown as ReadonlyArray<SystemSettingRow>) {
            if (row && typeof row.settingKey === 'string') {
              byKey.set(row.settingKey, row.settingValue ?? null);
            }
          }
          setStatus({
            enabled: asBool(byKey.get('rcp.enabled') ?? null),
            tenantId: byKey.get('rcp.tenantId') ?? null,
            tenantDomain: byKey.get('rcp.tenantDomain') ?? null,
            routePrefix: byKey.get('rcp.routePrefix') ?? null,
          });
        } else {
          setLoadError(resp.error ?? 'Failed to load system settings.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const tenantValue = useMemo(() => {
    if (!status) return '—';
    if (!status.tenantDomain && !status.tenantId) return '—';
    return `${status.tenantDomain ?? '—'} (${status.tenantId ?? '—'})`;
  }, [status]);

  return (
    <>
      <Banner tone="info">
        This panel is the future RCP build agent&apos;s checklist. Bill&apos;s binding rule
        (<code className="cell-mono">project_rcp_only_scaffolding_allowed</code>) keeps every
        staff-facing surface fully functional at launch; only the customer portal is allowed to be
        scaffolded. When Phase 2 starts, the five routes below switch from a polite
        {' '}<code className="cell-mono">phase-2-pending</code> payload to live customer flows.
      </Banner>

      {/* ── Master toggle + tenant binding (live) ─────────────────────────── */}
      <Card
        title="Master toggle + tenant binding"
        actions={
          <div className="card-actions-row">
            <Pill tone={status?.enabled ? 'ok' : 'muted'}>
              {status?.enabled ? 'Live' : 'Phase 2 — pending'}
            </Pill>
            <Button onClick={refresh}>Refresh</Button>
          </div>
        }
      >
        {loadError ? (
          <Banner tone="err">
            Could not load RCP settings: {loadError} <Button onClick={refresh}>Retry</Button>
          </Banner>
        ) : !status ? (
          <Spinner label="Loading RCP settings…" />
        ) : (
          <dl className="detail-grid">
            <dt className="detail-grid__label">RCP enabled</dt>
            <dd className="detail-grid__value">
              <span className="cell-strong">
                {status.enabled ? 'true (Live)' : 'false (Phase 2 pending)'}
              </span>
              <span className="cell-desc">
                {' '}— toggle via System → Settings (key{' '}
                <code className="cell-mono">rcp.enabled</code>). Read-only here.
              </span>
            </dd>

            <dt className="detail-grid__label">External tenant</dt>
            <dd className="detail-grid__value detail-grid__value--mono">{tenantValue}</dd>

            <dt className="detail-grid__label">Reserved route prefix</dt>
            <dd className="detail-grid__value detail-grid__value--mono">
              {status.routePrefix ?? '—'}
            </dd>
          </dl>
        )}
      </Card>

      {/* ── /v3/portal/* route table ──────────────────────────────────────── */}
      <Card title="/v3/portal/* route table (5 reserved)">
        <p className="modal-prose">
          Each route is wired today and returns a polite{' '}
          <code className="cell-mono">phase-2-pending</code> payload (200 OK — never 404 / 501) so
          the frontend can probe the customer surface without retrofit work later.
        </p>
        <DataTable<PortalRouteRow>
          columns={ROUTE_COLUMNS}
          rows={PORTAL_ROUTES}
          rowKey={(r) => r.path}
        />
      </Card>

      {/* ── Customer-as-actor SQL shape ───────────────────────────────────── */}
      <Card title="Customer-as-actor SQL shape (verified 2026-04-25)">
        <p className="modal-prose">
          The Wave 5.4 audit checked every column the RCP rule names. None require a migration; the
          existing free-text columns already accept the customer case. If a Phase-2 review later adds
          strict CHECK constraints, extend them via the{' '}
          <code className="cell-mono">16-rcp-*</code> seed family — do not bake the customer case
          into a migration.
        </p>
        <DataTable<SqlShapeRow>
          columns={SQL_COLUMNS}
          rows={SQL_SHAPE}
          rowKey={(r) => r.briefName}
        />
      </Card>

      {/* ── Phase-2 cutover checklist ─────────────────────────────────────── */}
      <Card title="Phase-2 cutover checklist">
        <ol className="modal-prose">
          <li>
            Ron creates the RCP Frontend SPA registration in{' '}
            <code className="cell-mono">pcbportal</code> per the AAD shape doc (path:{' '}
            <code className="cell-mono">{AAD_SHAPE_DOC}</code>).
          </li>
          <li>
            Decide whether to extend the existing <code className="cell-mono">mypcb</code> API
            audience for <code className="cell-mono">/v3/portal/*</code> calls or register a sibling
            API in <code className="cell-mono">pcbportal</code> (see AAD shape doc §1).
          </li>
          <li>
            Replace each route in <code className="cell-mono">PortalFunction.cs</code>: switch from{' '}
            <code className="cell-mono">AuthorizationLevel.Function</code> to AAD scope-based
            authorization, then drop in the live customer flow.
          </li>
          <li>
            Flip <code className="cell-mono">rcp.enabled</code> to <strong>true</strong> via System →
            Settings.
          </li>
          <li>
            If strict actor / origin enums are mandated later, ship the CHECK extension via{' '}
            <code className="cell-mono">17-rcp-*</code> seeds — do not write a migration.
          </li>
        </ol>
      </Card>
    </>
  );
}

export default RcpScaffoldPage;
